import { FC, useCallback, useMemo, useState } from "react";
import { GridApi } from "ag-grid-community";
import { Button, IconButton, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { DownloadOutlined } from "@mui/icons-material";
import { useEffectOnceWhen } from "rooks";

interface AGGridExportCSVProps {
  fileName: string;
  fullButton?: boolean;
  tooltipTitle?: string;
  gridApi: GridApi | null;
}

export const AGGridExportCSV: FC<AGGridExportCSVProps> = ({
  gridApi,
  tooltipTitle,
  fileName,
  fullButton,
}) => {
  const [disabled, setDisabled] = useState(!gridApi?.getDisplayedRowCount());

  const columnKeys = useMemo(
    () =>
      gridApi
        ?.getAllGridColumns()
        ?.map((col) => col.getColId())
        ?.filter(
          (id) =>
            id !== "actions" &&
            id !== "toFitEmptySpace" &&
            id !== "ag-Grid-SelectionColumn" &&
            id !== "ag-Grid-AutoColumn",
        ),
    [gridApi],
  );

  const exportDataAsCsv = useCallback(() => {
    gridApi?.exportDataAsCsv({
      fileName,
      columnKeys,

      skipRowGroups: true,
      skipColumnGroupHeaders: true,
    });
  }, [gridApi, fileName, columnKeys]);

  useEffectOnceWhen(() => {
    const onGridReady = () => {
      setDisabled(!gridApi?.getDisplayedRowCount());
    };

    onGridReady();
    gridApi?.addEventListener("rowDataUpdated", onGridReady);

    return () => {
      gridApi?.removeEventListener("rowDataUpdated", onGridReady);
    };
  }, !!gridApi);

  if (fullButton) {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={disabled}
        sx={exportButtonStyles}
        onClick={exportDataAsCsv}
        startIcon={<DownloadOutlined fontSize="small" />}
      >
        Export CSV
      </Button>
    );
  }

  return (
    <Tooltip
      arrow
      title={disabled ? "No Data To Export" : (tooltipTitle ?? "Export CSV")}
      placement="top"
    >
      <span>
        <IconButton onClick={exportDataAsCsv} size="small" disabled={disabled}>
          <DownloadOutlinedIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const exportButtonStyles = { width: 115 };
